import type { ChoreoLink } from './calendar'

/**
 * One practice video as a form holds it (D-541, D-668, D-670).
 *
 * Blank strings rather than nulls, because these are bound straight to inputs
 * and a controlled input with `value={null}` is React's uncontrolled warning.
 */
export type ChoreoLinkRow = { label: string; url: string; mediaAssetId: string | null }

/** What the server sent, ready to type into. */
export function asChoreoRow(link: ChoreoLink): ChoreoLinkRow {
  return { label: link.label ?? '', url: link.url ?? '', mediaAssetId: link.mediaAssetId }
}

/**
 * Back to what the server takes — <b>carrying `mediaAssetId`, never rebuilding
 * it</b> (D-668).
 *
 * A save sends the whole list and the server replaces it, so a field this
 * function forgets is a video deleted. That is not hypothetical: both editors
 * round-tripped `url` alone when uploads landed, which meant Tonya renaming a
 * routine silently removed every file a teacher had uploaded to it — and an
 * uploaded file, unlike a YouTube link, has no address to paste back in.
 * It is one function for the same reason: two copies of this drift, and the
 * second copy never receives the first one's fixes.
 *
 * `url` goes out null on a file row rather than the empty string the form
 * holds, because the server's own rule is that exactly one of the two is set.
 */
export function asChoreoInput(row: ChoreoLinkRow): ChoreoLink {
  return {
    label: row.label.trim() || null,
    url: row.mediaAssetId ? null : row.url.trim() || null,
    mediaAssetId: row.mediaAssetId,
  }
}

/** A row the teacher uploaded, rather than one she pasted — it has no editable address. */
export function isChoreoFile(row: ChoreoLinkRow): boolean {
  return row.mediaAssetId !== null
}

/**
 * Where a family plays a video the teacher uploaded (D-668, D-670).
 *
 * <b>Not a token, and deliberately not shareable.</b> The bytes stream back
 * through the app so the permission is checked on every request (D-109), which
 * is the whole reason there is no read SAS — these are videos of children, and
 * a link that works for whoever holds it is the thing being avoided.
 */
export function choreoFileHref(classId: string, mediaAssetId: string): string {
  return `/api/classes/${classId}/choreo/${mediaAssetId}`
}
